import React, { useState, useEffect } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import {
  TableContainer,
  Paper,
  Grid,
  TextField,
  Box,
  Typography,
  FormHelperText,
  Button,
  Tooltip,
  Dialog,
  Autocomplete,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  inputStyle,
  StyledButton,
  StyledTypography,
  StyledAgGridContainer,
  ViewButton,
  StyledButtonC,
  StyledSnackbar,
  StyledAlert,
  DeleteButton,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import ResponsiveTabs from "components/StyledComponents/styledComponents";
import Cookie from "js-cookie";
import URLs from "constants/urls";
import { useMaterialUIController } from "context";
import { CustomPaper } from "components/Styles/globalStyles";

export default function EmployeeAttendence() {
  const [controller] = useMaterialUIController();
  const { user } = controller;

  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("13:00");
  const [duration, setDuration] = useState("Full Day");
  const [activities, setActivities] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [employeeName, setEmployeeName] = useState("");
  const [employeeRecordId, setEmployeeRecordId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [attendanceData, setAttendanceData] = useState([]);
  const [calendarId, setCalendarId] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [months, setMonths] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState("");
  const [selectedPayPeriodId, setSelectedPayPeriodId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const hrms_token = Cookie.get("hrms_token");
  const MInput = inputStyle();
  const tabs = [{ label: "Attendance List" }, { label: "Mark Attendance" }];

  useEffect(() => {
    if (!user) {
      setAlert("Please Login Again", "error");
      return;
    }

    setEmployeeName(user.employeeName || "Employee");
    fetchEmployeeRecordId(user.employeeId);
    fetchCalendarId();
    fetchMonths();
    fetchEmployeesByCompany();
  }, [user]);

  useEffect(() => {
    if (startTime && endTime) {
      const start = new Date(`2000-01-01T${startTime}`);
      const end = new Date(`2000-01-01T${endTime}`);
      const diffHours = (end - start) / (1000 * 60 * 60);
      setDuration(diffHours <= 5 ? "Half Day" : "Full Day");
    }
  }, [startTime, endTime]);

  const fetchEmployeesByCompany = async () => {
    if (!user) {
      setAlert("Please Login Again", "error");
      return;
    }

    try {
      setLoadingEmployees(true);
      const url = `${URLs.employeeNamesByCompanyBase}?companyId=${user.companyId}`;
      console.log("→ Fetching employees from:", url);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: hrms_token ? `Bearer ${hrms_token}` : "",
        },
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        console.error("Employees fetch failed:", response.status, errorText);
        setAlert("Failed to load employees", "error");
        setEmployees([]);
        return;
      }

      let data = await response.json();
      console.log("Raw employees data:", data);

      // Normalize property names (fix common typos from backend)
      const normalizedEmployees = (Array.isArray(data) ? data : []).map((emp) => {
        const normalized = { ...emp };

        // Fix possible typo: employeeeRecordId → employeeRecordId
        if ("employeeeRecordId" in normalized && !("employeeRecordId" in normalized)) {
          normalized.employeeRecordId = normalized.employeeeRecordId;
          delete normalized.employeeeRecordId;
        }

        // Trim names to avoid display issues
        if (normalized.employeeName) {
          normalized.employeeName = normalized.employeeName.trim();
        }

        return normalized;
      });

      console.log("Normalized employees count:", normalizedEmployees.length);
      setEmployees(normalizedEmployees);
    } catch (err) {
      console.error("Employees API error:", err);
      setAlert("Could not load employees", "error");
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const fetchEmployeeRecordId = async (employeeId) => {
    if (!employeeId) return;

    try {
      const response = await fetch(`${URLs.getEmpPayRollData}${employeeId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        console.warn("Employee details fetch warning:", response.status);
        return;
      }

      const data = await response.json();
      if (data && data.employeeRecordId) {
        setEmployeeRecordId(data.employeeRecordId);
        // Optional: auto-load attendance for default/current pay period
        // if (selectedPayPeriodId) fetchAttendanceList(data.employeeRecordId, selectedPayPeriodId);
      } else {
        console.warn("No employee record ID found in response");
      }
    } catch (error) {
      console.warn("Error fetching employee details:", error);
    }
  };

  const fetchMonths = async () => {
    try {
      const response = await fetch(`${URLs.hrmsGetPayPeriods}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch pay periods");
      }

      const data = await response.json();
      setMonths(data);

      if (Array.isArray(data) && data.length > 0) {
        const firstPeriod = data[0];
        setSelectedPayPeriod(firstPeriod.payPeriodName);
        setSelectedPayPeriodId(firstPeriod.payPeriodId);
      }
    } catch (error) {
      console.error("Error fetching pay periods:", error);
      setAlert("Failed to fetch pay periods", "error");
    }
  };

  const handleDisplay = () => {
    if (!selectedPayPeriod || !selectedPayPeriodId) {
      setAlert("Please select a pay period", "error");
      return;
    }

    fetchAttendanceList(selectedPayPeriodId);
  };

  const fetchCalendarId = async () => {
    try {
      const response = await fetch(`${URLs.hrmsCalendarYearsUrl}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch calendar ID");
      }

      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setCalendarId(data[0].calendarId);
      }
    } catch (error) {
      console.error("Error fetching calendar ID:", error);
      setAlert("Failed to fetch calendar ID", "error");
    }
  };

  const fetchAttendanceList = async (payPeriodId) => {
    if (!payPeriodId) {
      setAttendanceData([]);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${URLs.hrmsGetAttendanceList}/${payPeriodId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });

      if (!response.ok) {
        console.warn("Attendance fetch warning:", response.status);
        setAttendanceData([]);
        return;
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        const formattedData = data.map((item) => ({
          date: item.date,
          startTime: item.startTime,
          endTime: item.endTime,
          employeeName: item.employeeName,
          dayCount: item.dayCount || "F",
          activites: item.activites || "",
          attendanceId: item.attendanceId,
          rawData: item,
        }));
        setAttendanceData(formattedData);
      } else {
        setAttendanceData([]);
      }
    } catch (error) {
      console.warn("Error fetching attendance:", error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const setAlert = (msg, type) => {
    setMessage(msg);
    setAlertType(type);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleEmployeeChange = (event, newValue) => {
    setSelectedEmployee(newValue);
    if (newValue) {
      setEmployeeName(newValue.employeeName);
      setEmployeeRecordId(newValue.employeeRecordId);
    } else {
      setEmployeeName("");
      setEmployeeRecordId(null);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!employeeRecordId) {
      setAlert("Employee Record ID is missing", "error");
      return;
    }

    const existingAttendance = attendanceData.find(
      (record) => record.date?.split("T")[0] === selectedDate
    );

    if (existingAttendance) {
      setAlert("Attendance already submitted for selected date", "error");
      return;
    }

    const selectedStartTime = "09:00";
    let selectedEndTime = duration === "Half Day" ? "14:00" : "19:00";

    const formatDateTime = (date, timeString) => {
      const [hours, minutes] = timeString.split(":");
      return `${date}T${hours}:${minutes}:00`;
    };

    const payload = {
      attendanceId: 0,
      calendarId: calendarId,
      employeeRecordId: employeeRecordId,
      startTime: formatDateTime(selectedDate, selectedStartTime),
      endTime: formatDateTime(selectedDate, selectedEndTime),
      dayCount: duration === "Full Day" ? "F" : "H",
      activites: activities || "Regular day",
      date: selectedDate,
    };

    setLoading(true);
    try {
      const response = await fetch(URLs.hrmsPostAttendanceUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to submit attendance");
      }

      setAlert("Attendance submitted successfully", "success");
      resetForm();
      fetchAttendanceList(selectedPayPeriodId);
      setActiveTab(0);
    } catch (error) {
      setAlert(`Failed to submit attendance: ${error.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setActivities("");
    setDuration("Full Day");
    setSelectedDate(new Date().toISOString().split("T")[0]);
    setSelectedEmployee(null);
    setEmployeeName("");
    setEmployeeRecordId(null);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "";
    const date = new Date(dateTimeString);
    if (isNaN(date.getTime())) return "";
    return date
      .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })
      .toUpperCase();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const columns = [
    {
      headerName: "Name",
      field: "employeeName",
      sortable: true,
      filter: true,
      flex: 1,
    },
    {
      headerName: "Date",
      field: "date",
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      headerName: "Duration",
      field: "dayCount",
      sortable: true,
      filter: true,
      flex: 1,
      valueFormatter: (params) => (params.value === "F" ? "Full Day" : "Half Day"),
    },
    {
      headerName: "Activities",
      field: "activites",
      sortable: true,
      filter: true,
      flex: 2,
    },
  ];

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", mt: 0 }}>
        <ResponsiveTabs
          tabs={tabs}
          activeTab={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          labelColor="secondary"
          bgColor="#e0f7fa"
        />

        {activeTab === 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
              <TextField
                select
                label="Select Pay Period"
                value={selectedPayPeriod}
                onChange={(e) => {
                  const selectedPeriodName = e.target.value;
                  setSelectedPayPeriod(selectedPeriodName);
                  const found = months.find((p) => p.payPeriodName === selectedPeriodName);
                  if (found) {
                    setSelectedPayPeriodId(found.payPeriodId);
                  } else {
                    setSelectedPayPeriodId("");
                  }
                }}
                sx={{ minWidth: 200, ...MInput.b }}
                SelectProps={{ native: true }}
              >
                <option value="">Select a pay period</option>
                {months.map((period) => (
                  <option key={period.payPeriodId} value={period.payPeriodName}>
                    {period.payPeriodName}
                  </option>
                ))}
              </TextField>
              <StyledButton onClick={handleDisplay}>Display</StyledButton>
            </Box>

            <StyledAgGridContainer
              component={Paper}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                height: "calc(100vh - 200px)",
                width: "100%",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <AgGridReact
                  rowData={attendanceData}
                  columnDefs={columns}
                  pagination={true}
                  paginationPageSize={10}
                  defaultColDef={{ resizable: true, filter: true }}
                  overlayNoRowsTemplate="<span>No attendance records found</span>"
                />
              )}
            </StyledAgGridContainer>
          </Box>
        )}

        {activeTab === 1 && (
          <StyledTableContainer
            container
            spacing={1}
            sx={{
              display: "flex",
              backgroundColor: "#ffffff",
              minHeight: "500px",
              borderRadius: "15px",
              overflow: "auto",
              width: "100%",
            }}
          >
            <Grid
              container
              spacing={1}
              sx={{ justifyContent: "center", alignItems: "center", width: "100%", margin: "auto" }}
            >
              <Grid item xs={12} md={12} lg={10}>
                <div style={{ position: "relative", width: "100%" }}>
                  <StyledTypography>Post Attendance</StyledTypography>
                  <Tooltip title="Reset">
                    <StyledButtonC onClick={resetForm}>
                      <RestartAltIcon />
                    </StyledButtonC>
                  </Tooltip>
                </div>

                <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                  <Grid container spacing={3}>
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                    >
                      <Autocomplete
                        options={employees}
                        getOptionLabel={(option) =>
                          (option.employeeName || option.name || "Unnamed Employee")?.trim() || ""
                        }
                        isOptionEqualToValue={(option, value) =>
                          (option.employeeRecordId || option.employeeeRecordId) ===
                          (value?.employeeRecordId || value?.employeeeRecordId)
                        }
                        value={selectedEmployee}
                        onChange={handleEmployeeChange}
                        loading={loadingEmployees}
                        PaperComponent={CustomPaper}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Employee Name"
                            required
                            sx={MInput.b}
                            InputLabelProps={{ sx: { color: MInput.a } }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {loadingEmployees ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                          />
                        )}
                      />
                      <TextField
                        type="date"
                        label="Date"
                        value={selectedDate}
                        onChange={(e) => setSelectedDate(e.target.value)}
                        fullWidth
                        required
                        sx={MInput.b}
                        InputLabelProps={{ shrink: true, sx: { color: MInput.a } }}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
                    >
                      <TextField
                        select
                        label="Duration"
                        value={duration}
                        onChange={(e) => setDuration(e.target.value)}
                        fullWidth
                        required
                        sx={MInput.b}
                        SelectProps={{ native: true }}
                        InputLabelProps={{ sx: { color: MInput.a } }}
                      >
                        <option value="Full Day">Full Day</option>
                        <option value="Half Day">Half Day</option>
                      </TextField>

                      <TextField
                        label="Activities"
                        multiline
                        rows={5}
                        value={activities}
                        onChange={(e) => setActivities(e.target.value)}
                        fullWidth
                        sx={MInput.b}
                        InputLabelProps={{ sx: { color: MInput.a } }}
                      />

                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <StyledButton type="submit">Submit</StyledButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </StyledTableContainer>
        )}
      </Box>

      <Dialog open={loading} onClose={() => setLoading(false)}>
        <DialogContent sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <CircularProgress color="primary" />
          <Typography variant="h6" sx={{ mt: 2 }}>
            Loading, please wait...
          </Typography>
        </DialogContent>
      </Dialog>

      <StyledSnackbar
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <StyledAlert onClose={handleClose} severity={alertType}>
          {message}
        </StyledAlert>
      </StyledSnackbar>
    </DashboardLayout>
  );
}
